import React from 'react'

interface FormFieldProps {
  children: React.ReactNode
  label?: string
  error?: string
  helperText?: string
  required?: boolean
  className?: string
}

export function FormField({
  children,
  label,
  error,
  helperText,
  required = false,
  className = '',
}: FormFieldProps) {
  return (
    <div className={`w-full ${className}`}>
      {label && (
        <FormLabel required={required}>{label}</FormLabel>
      )}
      {children}
      {error && <FormError>{error}</FormError>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  )
}

interface FormLabelProps {
  children: React.ReactNode
  required?: boolean
  className?: string
  htmlFor?: string
}

export function FormLabel({
  children,
  required = false,
  className = '',
  htmlFor,
}: FormLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={`block text-sm font-medium text-gray-700 mb-1 ${className}`}
    >
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  )
}

interface FormErrorProps {
  children: React.ReactNode
  className?: string
}

export function FormError({ children, className = '' }: FormErrorProps) {
  return (
    <p className={`mt-1 text-sm text-red-600 ${className}`} role="alert">
      {children}
    </p>
  )
}

interface FormSectionProps {
  title?: string
  description?: string
  children: React.ReactNode
  className?: string
}

export function FormSection({
  title,
  description,
  children,
  className = '',
}: FormSectionProps) {
  return (
    <div className={`space-y-4 ${className}`}>
      {(title || description) && (
        <div>
          {title && <h3 className="text-lg font-medium text-gray-900">{title}</h3>}
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
      )}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  )
}

interface FormActionsProps {
  children: React.ReactNode
  className?: string
}

export function FormActions({ children, className = '' }: FormActionsProps) {
  return (
    <div className={`flex items-center justify-end gap-3 pt-4 ${className}`}>
      {children}
    </div>
  )
}
